const API_BASE_URL = import.meta.env.VITE_AISHA_API_BASE || 'http://localhost:7999/api';

// Printfulカタログ関連の型定義
export interface PrintfulCatalogProduct {
  id: number;
  type: string;
  brand: string;
  model: string;
  image: string;
  variant_count: number;
  currency: string;
  description: string;
  variants?: PrintfulCatalogVariant[];
}

export interface PrintfulCatalogVariant {
  id: number;
  product_id: number;
  name: string;
  size: string;
  color: string;
  color_code: string;
  price: string;
  in_stock: boolean;
  availability_status: string;
}

export interface PrintfulCategory {
  id: number;
  parent_id: number | null;
  image_url: string;
  catalog_position: number;
  size: string;
  title: string;
}

// Sync Products関連の型定義
export interface PrintfulSyncProduct {
  id: string;
  external_id: string;
  name: string;
  synced: number;
  thumbnail_url?: string;
  is_ignored: boolean;
}

export interface PrintfulSyncVariant {
  id: string;
  external_id: string;
  sync_product_id: string;
  name: string;
  synced: boolean;
  variant_id: number;
  retail_price: string;
  currency: string;
  size?: string;
  color?: string;
  color_code?: string;
  product?: {
    variant_id: number;
    product_id: number;
    image: string;
    name: string;
  };
  files?: Array<{
    type: string;
    url: string;
    filename?: string;
  }>;
}

export interface PrintfulSyncProductDetail {
  sync_product: PrintfulSyncProduct;
  sync_variants: PrintfulSyncVariant[];
}

// API レスポンス型
export interface PrintfulApiResponse<T> {
  success: boolean;
  result?: T;
  error?: string;
  message?: string;
}

// カタログAPI
export const getPrintfulCatalogProducts = async (categoryId?: number) => {
  const params = categoryId ? `?category_id=${categoryId}` : '';
  const response = await fetch(`${API_BASE_URL}/printful/catalog/products/${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch Printful catalog products: ${response.statusText}`);
  }
  
  return response.json();
};

export const getPrintfulCatalogCategories = async () => {
  const response = await fetch(`${API_BASE_URL}/printful/catalog/categories/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch Printful categories: ${response.statusText}`);
  }
  
  return response.json();
};

// Sync Products API
export const getPrintfulSyncProducts = async (status?: string, search?: string) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  
  const queryString = params.toString();
  const url = queryString ? `${API_BASE_URL}/printful/sync-products/?${queryString}` : `${API_BASE_URL}/printful/sync-products/`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch Printful sync products: ${response.statusText}`);
  }
  
  return response.json();
};

export const getPrintfulSyncProduct = async (syncProductId: string) => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-products/${syncProductId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch Printful sync product: ${response.statusText}`);
  }
  
  return response.json();
};

export const createPrintfulSyncProduct = async (productData: {
  sync_product: {
    external_id?: string;
    name: string;
    thumbnail?: string;
  };
  sync_variants: Array<{
    external_id?: string;
    variant_id: number;
    retail_price: string;
    files?: Array<{
      type: string;
      url: string;
    }>;
  }>;
}) => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-products/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(productData),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to create Printful sync product: ${response.statusText}`);
  }
  
  return response.json();
};

export const updatePrintfulSyncProduct = async (
  syncProductId: string,
  productData: Partial<{
    sync_product: {
      name: string;
      thumbnail?: string;
    };
    sync_variants: Array<{
      id: string;
      retail_price: string;
    }>;
  }>
) => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-products/${syncProductId}/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(productData),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to update Printful sync product: ${response.statusText}`);
  }
  
  return response.json();
};

export const deletePrintfulSyncProduct = async (syncProductId: string) => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-products/${syncProductId}/`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to delete Printful sync product: ${response.statusText}`);
  }
  
  return response.json();
};

// バリアント価格更新
export const updatePrintfulVariantPrice = async (
  syncVariantId: string,
  retailPrice: number
) => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-variants/${syncVariantId}/price/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ retail_price: retailPrice }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to update Printful variant price: ${response.statusText}`);
  }
  
  return response.json();
};

// 全商品同期（送料情報付き）
export const syncAllPrintfulProducts = async () => {
  const response = await fetch(`${API_BASE_URL}/printful/sync-all/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to sync Printful products: ${response.statusText}`);
  }
  
  return response.json();
};